import React, { useCallback, useMemo, useRef } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { type TFunction } from 'i18next';

import {
  CreateSuborganizationRequest,
  FolderType,
  getGetOrganizationItemsQueryKey,
  getGetOrganizationStructureQueryKey,
  GetOrganizationItemsParams,
  useActivateSuborganization,
  useCreateSuborganization,
  useGetSuborganization,
  useUpdateSuborganization
} from '@/api/generated';
import SubOrganizationBigIcon from '@/assets/icons/hierarchy-chart-suborg.svg?react';
import BackdropLoader from '@/components/BackdropLoader';
import { useSnackbar } from '@/hooks/useSnackbar';
import { borders } from '@/theme/constants';
import SettingsModalForm, { SettingsModalFormRef } from '@/views/SettingsModalForm/SettingsModalForm';

import CreateOrganizationItemModalContent from './CreateOrganizationItemModalContent';
import CreateOrganizationItemModalHeader from './CreateOrganizationItemModalHeader';
import { getCreateSubOrgBasicTabSchema, getCreateSubOrgMoreInfoTabSchema } from './schema';
import SubOrgBasicInfoTab from './SubOrgBasicInfoTab';
import SubOrgMoreInfoTab from './SubOrgMoreInfoTab';
import { CreateOrganizationItemFormData } from './types';

const getSteps = (t: TFunction) => [
  {
    content: SubOrgBasicInfoTab,
    title: t('layouts.organizations.create.tabs.basicInfo'),
    schema: getCreateSubOrgBasicTabSchema(t)
  },
  {
    content: SubOrgMoreInfoTab,
    title: t('layouts.organizations.create.tabs.moreInfo'),
    schema: getCreateSubOrgMoreInfoTabSchema(t)
  }
  // TODO: Uncomment once users/roles ready
  // {
  //   content: CommonUsersAccessTab,
  //   title: t('layouts.organizations.create.tabs.users'),
  //   schema: getEmptySchema()
  // }
];

const getGlobalSchema = (t: TFunction) => getCreateSubOrgBasicTabSchema(t).concat(getCreateSubOrgMoreInfoTabSchema(t));

const DEFAULT_VALUES = {
  name: ''
};

interface CreateSubOrganizationModalProps {
  isOpen?: boolean;
  onClose: () => void;
  currentFilter?: GetOrganizationItemsParams | null;
  type?: FolderType;
  locationSlug?: string | null;
  organizationSlug?: string;
  draftItemSlug?: string | null;
}

const CreateSubOranizationModal: React.FC<CreateSubOrganizationModalProps> = ({
  isOpen = true,
  onClose,
  type,
  locationSlug,
  organizationSlug,
  currentFilter,
  draftItemSlug
}) => {
  const { t } = useTranslation(['organizations', 'common', 'validation']);
  const { showMessage } = useSnackbar();
  const formRef = useRef<SettingsModalFormRef<FieldValues>>(null);
  const queryClient = useQueryClient();

  const { data: draftItemData, isLoading: isSubOrganizationLoading } = useGetSuborganization(
    organizationSlug!,
    draftItemSlug!
  );

  const { mutateAsync: createSubOrganizationDraft, isLoading: isSubOrganizationCreating } = useCreateSuborganization();
  const { mutateAsync: activateSuborganization, isLoading: isSubOrganizationActivating } = useActivateSuborganization();
  const { mutateAsync: updateSubOrganization, isLoading: isSubOrganizationUpdating } = useUpdateSuborganization();

  const isLoading =
    isSubOrganizationCreating || isSubOrganizationActivating || isSubOrganizationUpdating || isSubOrganizationLoading;

  const handleSubOrganizationSave = useCallback(
    async (shouldActivate: boolean) => {
      const values = formRef?.current?.getValues() as CreateOrganizationItemFormData;
      const office = values?.offices as string | null;
      const country = values?.countries as string | null;
      const data: CreateSuborganizationRequest = {
        locales: ['en-US'], // FIXME not sure why is it required
        name: values?.name,
        legalName: values?.legalName,
        offices: office ? [office] : undefined,
        countries: (country ? [country] : undefined) as string[],
        keyAccountManagers: values?.keyAccountManagers as string[],
        employeeCount: values?.employeeCount,
        legacyId: values?.legacyId,
        jiraId: values?.jiraId,
        pipedriveId: values?.pipedriveId
        // TODO: Add assignments once ready on backend
        // assignments: values?.assignments ?? []
      };
      let itemSlugToActivate = draftItemSlug;
      if (!itemSlugToActivate) {
        const result = await createSubOrganizationDraft({
          slug: organizationSlug!,
          data
        });
        await queryClient.invalidateQueries(getGetOrganizationItemsQueryKey(organizationSlug!, currentFilter!));
        await queryClient.invalidateQueries(getGetOrganizationStructureQueryKey(organizationSlug!));
        itemSlugToActivate = result?.slug;
      } else {
        await updateSubOrganization({
          slug: organizationSlug!,
          subSlug: itemSlugToActivate,
          data
        });
      }
      if (shouldActivate && itemSlugToActivate) {
        await activateSuborganization({
          slug: organizationSlug!,
          subSlug: itemSlugToActivate
        });
      }
    },
    [
      activateSuborganization,
      createSubOrganizationDraft,
      currentFilter,
      draftItemSlug,
      organizationSlug,
      queryClient,
      updateSubOrganization
    ]
  );

  const handleSaveDraft = useCallback(async () => {
    try {
      await handleSubOrganizationSave(false);
      showMessage(t('layouts.organizations.create.draftSaved'));
      onClose();
      if (organizationSlug && currentFilter) {
        await queryClient.invalidateQueries(getGetOrganizationItemsQueryKey(organizationSlug!, currentFilter!));
      }
    } catch (error) {
      showMessage(t('layouts.organizations.create.alerts.error'));
    }
  }, [currentFilter, handleSubOrganizationSave, onClose, organizationSlug, queryClient, showMessage, t]);

  const handleActivate = useCallback(async () => {
    try {
      await handleSubOrganizationSave(true);
      showMessage(t('layouts.organizations.create.alerts.success'));

      if (organizationSlug && currentFilter) {
        await queryClient.invalidateQueries(getGetOrganizationItemsQueryKey(organizationSlug!, currentFilter!));
      }
    } catch (error) {
      showMessage(t('layouts.organizations.create.alerts.error'));
    }
  }, [currentFilter, handleSubOrganizationSave, organizationSlug, queryClient, showMessage, t]);

  const defaultValues = useMemo(
    () =>
      parent &&
      organizationSlug &&
      (!draftItemSlug || draftItemData) && {
        ...(draftItemSlug
          ? {
              name: draftItemData?.name,
              legalName: draftItemData?.legalName,
              offices: draftItemData?.offices?.[0],
              countries: draftItemData?.countries?.[0],
              keyAccountManagers: draftItemData?.keyAccountManagers?.filter(user => user?.id)?.map(user => user.id!),
              employeeCount: draftItemData?.employeeCount,
              legacyId: draftItemData?.legacyId,
              pipedriveId: draftItemData?.pipedriveId,
              jiraId: draftItemData?.jiraId
            }
          : DEFAULT_VALUES),
        organization: organizationSlug
      },
    [draftItemData, draftItemSlug, organizationSlug]
  );

  const steps = useMemo(() => getSteps(t), [t]);

  const schema = useMemo(() => getGlobalSchema(t), [t]);

  if (!defaultValues) return <BackdropLoader />;

  return (
    <SettingsModalForm
      ref={formRef}
      isOpen={isOpen}
      onClose={onClose}
      defaultValues={defaultValues}
      onSave={handleActivate}
      isSaving={isLoading}
      validation={schema}
      head={
        <CreateOrganizationItemModalHeader
          Icon={SubOrganizationBigIcon}
          title={t('layouts.organizations.create.items.suborganization')}
          organizationSlug={organizationSlug}
          locationSlug={locationSlug}
          type={type}
        />
      }
      sx={{
        '&.CreateOrganizationModal-root .SettingsModalForm-root': {
          maxWidth: 600,
          minHeight: 'auto',
          height: 'auto'
        },
        '&.CreateOrganizationModal-root .MuiDialogTitle-root': {
          py: 0,
          borderBottom: borders.default,
          borderColor: 'grey.300'
        }
      }}
      className="CreateOrganizationModal-root"
    >
      <CreateOrganizationItemModalContent
        draftExists={!!draftItemSlug}
        handleSaveDraft={handleSaveDraft}
        isLoading={isLoading}
        steps={steps}
        title={t('layouts.organizations.create.items.suborganization')}
        draftAllowed
      />
    </SettingsModalForm>
  );
};

export default CreateSubOranizationModal;
